/* eslint-disable camelcase */
import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TableHead,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import ModernMuiTable from '../components/ModernMuiTable';
import useAxiosWithAuth from '../api/useAxiosWithAuth';

const StockTransfers = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [transfers, setTransfers] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [warehousesLoading, setWarehousesLoading] = useState(true);

  const apiRef = useRef();
  const { get, post } = useAxiosWithAuth();

  // Store API methods in ref to avoid dependency issues
  useEffect(() => {
    apiRef.current = { get, post };
  }, [get, post]);

  // Fetch warehouse data
  // useEffect(() => {
  //   const fetchWarehouses = async () => {
  //     try {
  //       setWarehousesLoading(true);
  //       const response = await apiRef.current.get('/unleashed/warehouses?page_size=9999999&page_number=1');

  //       if (response.data?.Items) {
  //         setWarehouses(response.data.Items);
  //       } else {
  //         setWarehouses([]);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching warehouses:', error);
  //       setWarehouses([]);
  //     } finally {
  //       setWarehousesLoading(false);
  //     }
  //   };

  //   fetchWarehouses();
  // }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Use POST request with warehouse code in body
        const response = await apiRef.current.post(
          `/supplier/supplier-report-for-stock-transfers/Little%20Green%20Pharma?page_size=${rowsPerPage}&page_number=${
            page + 1
          }`,
          {
            warehouse_code: selectedWarehouse || '',
          }
        );

        if (response.data) {
          setTransfers(response.data.payload || []);
          setPaginationMeta({
            total: response.data.meta?.total_size || 0,
          });
        } else {
          setTransfers([]);
          setPaginationMeta({ total: 0 });
        }
      } catch (error) {
        console.error('Error fetching stock transfers:', error);
        setTransfers([]);
        setPaginationMeta({ total: 0 });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage, selectedWarehouse]);

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Function to export data as CSV
  const exportToCSV = async () => {
    try {
      setIsExporting(true); // Set export loading state

      // Fetch all data with a large page size
      const response = await apiRef.current.post(
        '/supplier/supplier-report-for-stock-transfers/Little%20Green%20Pharma?page_size=9999999&page_number=1',
        {
          warehouse_code: selectedWarehouse || '',
        }
      );

      const allTransfers = response.data.payload || [];

      if (allTransfers.length === 0) {
        // If no data is available, just return
        return;
      }

      // Define CSV headers
      const headers = [
        'Transfer Order Number',
        'Product Code',
        'Quantity',
        'Origin Warehouse Code',
        'Origin Warehouse Name',
        'Destination Warehouse Code',
        'Destination Warehouse Name',
        'Batch Number',
        'Batch Expiry',
        'Batch Quantity',
      ];

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...allTransfers.map((transfer) =>
          [
            transfer.transfer_order_number ? `"${transfer.transfer_order_number}"` : '""',
            transfer.product_code ? `"${transfer.product_code}"` : '""',
            transfer.quantity || '',
            transfer.origin_warehouse_code ? `"${transfer.origin_warehouse_code}"` : '""',
            transfer.origin_warehouse_name ? `"${transfer.origin_warehouse_name}"` : '""',
            transfer.destination_warehouse_code ? `"${transfer.destination_warehouse_code}"` : '""',
            transfer.destination_warehouse_name ? `"${transfer.destination_warehouse_name}"` : '""',
            transfer.batch_number ? `"${transfer.batch_number}"` : '""',
            transfer.batch_expiry ? `"${transfer.batch_expiry}"` : '""',
            transfer.batch_quantity || '',
          ].join(',')
        ),
      ].join('\n');

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `stock-transfers-${new Date().toISOString().slice(0, 10)}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setIsExporting(false); // Reset export loading state
    }
  };

  return (
    <Page title="Stock Transfers">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Stock Transfers
          </Typography>
        </Stack>

        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '16px',
            }}
          >
            {/* <FormControl sx={{ minWidth: 200, mr: 2, mb: 1 }} size="small">
              <InputLabel id="warehouse-filter-label">Filter by Warehouse</InputLabel>
              <Select
                labelId="warehouse-filter-label"
                id="warehouse-filter"
                value={selectedWarehouse}
                label="Filter by Warehouse"
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                disabled={warehousesLoading}
              >
                <MenuItem value="">
                  <em>All Warehouses</em>
                </MenuItem>
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.WarehouseCode} value={warehouse.WarehouseCode}>
                    {warehouse.WarehouseName} ({warehouse.WarehouseCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
            <Button
              variant="outlined"
              onClick={exportToCSV}
              startIcon={isExporting ? null : <Iconify icon="solar:export-bold" />}
              style={{ marginBottom: '16px', height: '40px' }}
              disabled={isLoading || isExporting} // Disable when loading or exporting
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>

          <Scrollbar>
            <ModernMuiTable
              containerSx={{ minWidth: 800 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Transfer Order Number</TableCell>
                  <TableCell>Product Code</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Origin Warehouse Code</TableCell>
                  <TableCell>Origin Warehouse Name</TableCell>
                  <TableCell>Destination Warehouse Code</TableCell>
                  <TableCell>Destination Warehouse Name</TableCell>
                  <TableCell>Batch Number</TableCell>
                  <TableCell>Batch Expiry</TableCell>
                  <TableCell>Batch Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                          <CircularProgress size={48} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {transfers.length > 0 ? (
                        transfers.map((transfer, index) => (
                          <TableRow key={transfer.transfer_order_number || index}>
                            <TableCell>{transfer.transfer_order_number}</TableCell>
                            <TableCell>{transfer.product_code}</TableCell>
                            <TableCell>{transfer.quantity}</TableCell>
                            <TableCell>{transfer.origin_warehouse_code}</TableCell>
                            <TableCell>{transfer.origin_warehouse_name}</TableCell>
                            <TableCell>{transfer.destination_warehouse_code}</TableCell>
                            <TableCell>{transfer.destination_warehouse_name}</TableCell>
                            <TableCell>{transfer.batch_number || ''}</TableCell>
                            <TableCell>{transfer.batch_expiry || ''}</TableCell>
                            <TableCell>{transfer.batch_quantity}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            No stock transfers found
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </ModernMuiTable>
            </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={paginationMeta.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default StockTransfers;
